import { IOrderProduct } from "../types";

/**
 * Collects all image URLs from order cargo items
 * @param cargo Array of cargo/order product items
 * @returns Array of image URLs or empty array if none found
 */
export const getCargoImageUrls = (
  cargo: IOrderProduct[] | undefined
): string[] => {
  if (!cargo || !Array.isArray(cargo) || cargo.length === 0) {
    return [];
  }

  const allImages: string[] = [];
  
  cargo.forEach(item => {
    if (item.imgUrls && Array.isArray(item.imgUrls)) {
      allImages.push(...item.imgUrls.filter(url => url && typeof url === 'string'));
    }
  });
  
  return allImages;
};

/**
 * Handles image loading errors by logging the error
 * @param event The error event from image loading
 */
export const handleImageError = (
  event: React.SyntheticEvent<HTMLImageElement, Event>
): void => {
  const img = event.currentTarget;
  console.warn(`Failed to load image: ${img.src}`);
  // Hide the image on error instead of replacing it
  img.style.display = 'none';
}; 

/**
 * Compresses an image file and returns a base64 string
 * @param file The image file to compress
 * @param maxWidth Maximum width for the compressed image
 * @param maxHeight Maximum height for the compressed image
 * @param quality JPEG quality (0-1)
 * @returns Promise<string> Base64 string of compressed image
 */
export const compressImage = (
  file: File,
  maxWidth: number = 800,
  maxHeight: number = 600,
  quality: number = 0.8
): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("File is not an image"));
      return;
    }

    const img = new Image();
    const reader = new FileReader();

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(width));
      canvas.height = Math.max(1, Math.round(height));
      const ctx = canvas.getContext("2d");

      // Draw and compress
      if (ctx) {
        // Preserve original format to maintain transparency for PNG files
        const mimeType = file.type === "image/png" ? "image/png" : "image/jpeg";
        if (mimeType === "image/jpeg") {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const compressedDataUrl = canvas.toDataURL(mimeType, quality);
        resolve(compressedDataUrl);
      } else {
        reject(new Error("Failed to get canvas context"));
      }
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    reader.onerror = () => reject(new Error("Failed to read image"));
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        reject(new Error("Failed to read image"));
        return;
      }
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
};
